import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Avatar, Modal, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { stringToColor } from '../../../shared/commonFunctions';
import TicketRow from '../../../components/TicketRow';
import { reportStyles } from '../../../shared/globalSX';
import { getStatusColor, hexToRgba } from '../../dashboard/calendar-view/calendarColorUtils';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { getStatusLabel } from '../../../shared/utils/status';

const formatTicketForModal = (ticket, userName) => {
  const [prefix, num] = ticket.ticket_key ? ticket.ticket_key.split('-') : ['', ''];
  const status = getStatusLabel(ticket?.status);
  const assignees = Array.isArray(ticket.assignees) && ticket.assignees.length > 0
    ? ticket.assignees
    : [userName];
  return {
    ...ticket,
    ticket_number_prefix: prefix || '',
    ticket_number: num || '',
    title: ticket.title || '',
    assignees,
    assignee_name: assignees.join(', '),
    due_date: ticket.due_date || null,
    status,
    priority: typeof ticket.priority === 'object' ? ticket.priority?.name || 'Medium' : ticket.priority || 'Medium'
  };
};

const TicketChip = ({ ticket, extraCount = 0, onClickExtra, className = '' }) => {
  const { t } = useTranslation();
  const status = getStatusLabel(ticket?.status);
  const statusColorFromApi = typeof ticket?.status === 'object' ? (ticket.status?.colour || ticket.status?.color) : null;
  const colorHex = statusColorFromApi || getStatusColor(status);
  
  const displayTitle = `${ticket.ticket_key} ${ticket.title}`;

  return (
    <Box 
      className={`ticket-chip ${className}`.trim()} 
      style={{ 
        backgroundColor: hexToRgba(colorHex, 0.08), 
        color: colorHex,
        border: `1px solid ${hexToRgba(colorHex, 0.18)}`
      }}
    >
      <Box className="ticket-chip-wrapper">
        <OverflowTooltip 
          title={displayTitle} 
          placement="top"
          className={`ticket-chip-title ${extraCount > 0 ? 'has-more' : ''}`}
          onClick={() => window.open(`/task/${ticket.ticket_key}`, '_blank')}
        >
          {displayTitle}
        </OverflowTooltip>
      </Box>
      {extraCount > 0 && (
        <Typography 
          className="ticket-chip-more"
          onClick={onClickExtra}
        >
          {t('common.nMore', { count: extraCount })}
        </Typography>
      )}
    </Box>
  );
};

const TeamUpdates = ({ updates = [] }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTickets, setModalTickets] = useState([]);
  const [modalTitle, setModalTitle] = useState('');

  const openTicketsModal = (tickets, title, userName) => {
    setModalTickets(tickets.map(t => formatTicketForModal(t, userName)));
    setModalTitle(title);
    setModalOpen(true);
  };

  const renderTickets = (tickets, title, userName) => {
    if (!tickets || tickets.length === 0) {
      return (
        <Typography className="empty-text">
          {t('dailyStandup.teamDetail.noUpdate')}
        </Typography>
      );
    }

    return (
      <Box className="render-tickets-wrapper">
        <Box className="ticket-chip-list">
          {tickets.map((ticket, index) => (
            <TicketChip
              key={`${ticket.ticket_key}-${index}`}
              ticket={ticket}
              extraCount={index === 0 ? tickets.length - 1 : 0}
              onClickExtra={() => openTicketsModal(tickets, title, userName)}
              className="ticket-chip-item"
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography className="section-title" sx={{ mb: '0 !important' }}>
          {t('dailyStandup.teamDetail.title')}
        </Typography>
        <Tooltip title={t('dailyStandup.teamDetail.datesInfoTooltip')} arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, cursor: 'pointer' }} />
        </Tooltip>
      </Box>
      <Box className="team-updates-container">
        <Box className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th className="col-developer">{t('dailyStandup.teamDetail.columns.developer')}</th>
                <th className="center col-yesterday">{t('dailyStandup.teamDetail.columns.yesterdayPlan')}</th>
                <th className="center col-today">{t('dailyStandup.teamDetail.columns.todayPlan')}</th>
                <th className="center col-status">{t('dailyStandup.teamDetail.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {updates.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-state-cell">
                    <Box className="empty-state-row">
                      <InfoOutlinedIcon fontSize="small" />
                      <Typography variant="body2" className="empty-state-text">
                        {t('dailyStandup.teamDetail.noUpdates')}
                      </Typography>
                    </Box>
                  </td>
                </tr>
              ) : (
                updates.map((update, idx) => {
                  // Determine row status from the first today ticket only; null if no tickets
                const firstTodayTicket = update.today_plan?.[0];
                  const fallbackStatus = firstTodayTicket ? getStatusLabel(firstTodayTicket?.status) : null;
                  const finalLabel = update.status ? getStatusLabel(update.status) : fallbackStatus;
                  const finalLabelText = finalLabel;

                  return (
                    <tr key={update.id} className={idx === updates.length - 1 ? 'last-row' : ''}>
                      <td>
                        <Box className="user-info">
                          <Avatar 
                            className="user-avatar"
                            style={{ backgroundColor: stringToColor(update.name) }}
                          >
                            {update.avatar}
                          </Avatar>
                          <OverflowTooltip 
                            title={update.name} 
                            placement="top"
                            className="user-name"
                          >
                            {update.name}
                          </OverflowTooltip>
                        </Box>
                      </td>
                      <td className="center">
                        {renderTickets(update.completed_yesterday, t('dailyStandup.teamDetail.columns.yesterdayPlan') + ` for ${update.name}`, update.name)}
                      </td>
                      <td className="center">
                        {renderTickets(update.today_plan, t('dailyStandup.teamDetail.columns.todayPlan') + ` for ${update.name}`, update.name)}
                      </td>
                      <td className="center">
                        {finalLabel ? (
                          <OverflowTooltip 
                            title={finalLabelText} 
                            placement="top"
                            component={Box}
                            className="ticket-chip status-chip-cell" 
                            style={{ 
                              backgroundColor: hexToRgba(getStatusColor(finalLabel), 0.08), 
                              color: getStatusColor(finalLabel),
                              border: `1px solid ${hexToRgba(getStatusColor(finalLabel), 0.18)}`
                            }}
                          >
                            {finalLabelText}
                          </OverflowTooltip>
                        ) : (
                          <Typography className="empty-text">—</Typography>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
              </tbody>
            </table>
          </Box>
      </Box>

      {/* Tickets Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={reportStyles.ticketsModalBox}>
          <Box sx={reportStyles.ticketsModalHeader}>
            <OverflowTooltip title={modalTitle} placement="bottom">
              <Typography variant="h6" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 2 }}>
                {modalTitle}
              </Typography>
            </OverflowTooltip>
            <IconButton onClick={() => setModalOpen(false)} size="small" sx={{ flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={reportStyles.ticketsModalContent} className="tickets-modal-container">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className="col-title">{t('dailyStandup.teamDetail.modalColumns.title')}</TableCell>
                    <TableCell className="col-assignee">{t('dailyStandup.teamDetail.modalColumns.assignee')}</TableCell>
                    <TableCell className="col-due-date">{t('dailyStandup.teamDetail.modalColumns.dueDate')}</TableCell>
                    <TableCell className="col-status">{t('dailyStandup.teamDetail.modalColumns.status')}</TableCell>
                    <TableCell className="col-priority">{t('dailyStandup.teamDetail.modalColumns.priority')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalTickets.map((ticket, idx) => (
                    <TicketRow key={`${ticket.ticket_number_prefix}-${ticket.ticket_number}-${idx}`} ticket={ticket} showDueDate={true} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TeamUpdates;
